import { z } from 'zod';

import { UnavailableSectionSchema } from './sbc-customer';
import { ProductStockSchema, WarehouseStockSchema } from './sbc-product-stock';

const UnidentifiedSchema = z.object({
  identified: z.literal(false),
  reason: z.string(),
});

export const BEAUTY_SKIN_SOURCES = ['skin-profile', 'quiz'] as const;

export const BeautySkinSchema = z.object({
  source: z.enum(BEAUTY_SKIN_SOURCES),
  primarySkinType: z.string().nullable(),
  skinTypes: z.array(z.string()),
  skinAge: z.string().nullable(),
  concerns: z.array(z.string()),
  frustrations: z.array(z.string()),
  inHerWords: z.string().nullable(),
  personalColor: z.string().nullable(),
  quizComplete: z.boolean(),
  updatedAt: z.string().nullable(),
});
export type BeautySkin = z.infer<typeof BeautySkinSchema>;

export const BeautySensitivitiesSchema = z.object({
  allergies: z.array(z.string()),
  allergyNotes: z.string().nullable(),
  avoidedIngredients: z.array(z.string()),
  avoidedIngredientNotes: z.string().nullable(),
  medicalFlags: z.array(z.string()),
  reactedToTypes: z.array(z.string()),
  reactionNotes: z.string().nullable(),
  answeredAt: z.string().nullable(),
});
export type BeautySensitivities = z.infer<typeof BeautySensitivitiesSchema>;

export const BeautyPreferencesSchema = z.object({
  productInterests: z.array(z.string()),
  lovedTypes: z.array(z.string()),
  lovedNotes: z.string().nullable(),
  wantAgain: z.array(z.string()),
});

export const BeautyDislikesSchema = z.object({
  avoidedTypes: z.array(z.string()),
  notForMe: z.array(z.string()),
});

export const BeautyProfileResultSchema = z.union([
  z.object({
    identified: z.literal(true),
    skin: BeautySkinSchema.nullable(),
    sensitivities: BeautySensitivitiesSchema.nullable(),
    preferences: BeautyPreferencesSchema,
    dislikes: BeautyDislikesSchema,
    lastUpdatedAt: z.string().nullable(),
  }),
  UnidentifiedSchema,
]);
export type BeautyProfileResult = z.infer<typeof BeautyProfileResultSchema>;

export const PRODUCT_INFO_MATCH_LIMIT = 3;

export const ProductIngredientsSchema = z.object({
  kind: z.enum(['full', 'key']),
  text: z.string(),
});

export const ProductInfoSchema = z.object({
  itemId: z.uuid(),
  brand: z.string().nullable(),
  name: z.string(),
  category: z.string().nullable(),
  size: z.string().nullable(),
  premium: z.boolean(),
  ingredients: ProductIngredientsSchema.nullable(),
  ingredientFlags: z.array(z.string()),
  description: z.string().nullable(),
  directions: z.string().nullable(),
  addOnPriceUsd: z.number().nullable(),
  inCurrentCatalog: z.boolean(),
  stock: z.object({ us: WarehouseStockSchema, kr: WarehouseStockSchema }),
  inStock: z.boolean(),
});
export type ProductInfo = z.infer<typeof ProductInfoSchema>;

export const ProductInfoResultSchema = z.object({
  query: z.string(),
  matches: z.array(ProductInfoSchema).max(PRODUCT_INFO_MATCH_LIMIT),
});
export type ProductInfoResult = z.infer<typeof ProductInfoResultSchema>;

export const CURATION_OPTION_LIMIT = 15;

export const CURATION_WINDOW_STATES = ['not-open', 'open', 'closed'] as const;
export type CurationWindowState = (typeof CURATION_WINDOW_STATES)[number];

export const CurationBoxSchema = z.object({
  month: z.string(),
  name: z.string().nullable(),
  opensOn: z.string().nullable(),
  curateBy: z.string(),
  shipsOn: z.string(),
  window: z.enum(CURATION_WINDOW_STATES),
});

export const CurationOrderSchema = z.object({
  number: z.string().nullable(),
  status: z.string(),
  paymentStatus: z.string(),
  parcelStatus: z.string(),
  placedAt: z.string().nullable(),
  autoCurated: z.boolean(),
});

export const CurationDraftSchema = z.object({
  status: z.string(),
  savedAt: z.string().nullable(),
});

export const CurationPickSchema = z.object({
  itemId: z.uuid(),
  name: z.string(),
  category: z.string().nullable(),
  premium: z.boolean(),
  quantity: z.number(),
  aiPicked: z.boolean(),
  wantMore: z.boolean(),
  aiReason: z.string().nullable(),
});
export type CurationPick = z.infer<typeof CurationPickSchema>;

export const CurationAddOnSchema = z.object({
  itemId: z.uuid(),
  name: z.string(),
  quantity: z.number(),
});

export const CurationAllowanceSchema = z.object({
  rule: z.literal('budget'),
  picks: z.number(),
  premiumPicks: z.number(),
  budgetUsedPercent: z.number().nullable(),
  configurator: z.object({ freePicks: z.number() }).nullable(),
});

export const AUTO_CURATION_STATES = [
  'not-needed',
  'not-run',
  'in-progress',
  'awaiting-approval',
  'built',
  'not-built',
] as const;
export type AutoCurationState = (typeof AUTO_CURATION_STATES)[number];

export const AutoCurationDecisionSchema = z.object({
  eligibility: z.string().nullable(),
  decidedAt: z.string().nullable(),
  failedStep: z.string().nullable(),
  failedReason: z.string().nullable(),
});

export const AutoCurationSchema = z.object({
  state: z.enum(AUTO_CURATION_STATES),
  jobStatus: z.string().nullable(),
  decision: AutoCurationDecisionSchema.nullable(),
});

export const CURATION_OPTION_SCOPES = ['her-pick-categories', 'whole-catalog'] as const;

export const CurationOptionSchema = ProductStockSchema.extend({
  premium: z.boolean(),
  fitsBudget: z.boolean().nullable(),
  ingredientFlags: z.array(z.string()),
});

export const CurationOptionsSchema = z.object({
  scope: z.enum(CURATION_OPTION_SCOPES),
  items: z.array(CurationOptionSchema).max(CURATION_OPTION_LIMIT),
});

export const CurationStatusResultSchema = z.union([
  z.object({
    identified: z.literal(true),
    box: CurationBoxSchema,
    started: z.boolean(),
    confirmed: z.boolean(),
    order: CurationOrderSchema.nullable(),
    draft: CurationDraftSchema.nullable(),
    picks: z.array(CurationPickSchema),
    addOns: z.array(CurationAddOnSchema),
    allowance: CurationAllowanceSchema,
    autoCuration: AutoCurationSchema,
    options: z.union([CurationOptionsSchema, UnavailableSectionSchema]),
  }),
  z.object({ identified: z.literal(true), box: z.null() }),
  UnidentifiedSchema,
]);
export type CurationStatusResult = z.infer<typeof CurationStatusResultSchema>;
